import type { SceneGroup } from '@lumen5/luminary';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { resolveSceneGroupById, sceneGroupDisplayId } from './scene-group-addressing';
import { VideoStateTool } from './VideoStateTool';

import {
    cutFillerWordsOperation,
    cutSilencesOperation,
} from 'js/src/data/transcript/transcript-operations';
import { getEditorFromSceneGroupId } from 'js/src/data/transcript/transcript-selectors';
import { MIN_CUTTABLE_SILENCE_DURATION } from 'js/src/data/transcript/transcript-types';
import { pluralize } from 'js/src/libs/custom-utils';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    isAIAvatarSceneGroup,
    isAIVoiceoverSceneGroup,
    isHumanVoicedrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';

const formatSeconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

export const cleanUpRecordingSchema = z.object({
    remove: z
        .array(z.enum(['filler_words', 'pauses']))
        .min(1)
        .describe(
            'What to cut out of the recording. filler_words: the hesitation sounds the transcript flags as fillers (um, uh, hmm, er and the like), not words that carry meaning such as "like", "so" or "you know". pauses: silences of a second or more between words; shorter pauses are the natural rhythm of speech and stay.',
        ),
    scene_group_ids: z
        .array(z.string())
        .optional()
        .describe(
            'The scene_group_ids of the recorded voiceover or talking-head groups to clean up. Omit to clean up every recording in the video.',
        ),
});
export type CleanUpRecordingArgs = z.infer<typeof cleanUpRecordingSchema>;

export class CleanUpRecordingTool extends VideoStateTool<typeof cleanUpRecordingSchema> {
    protected override readonly schema = cleanUpRecordingSchema;
    protected override readonly toolName = TOOL_NAMES.cleanUpRecording;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription = `Cut filler words (um, uh, hmm…) and/or long pauses out of recorded voiceover and talking-head recordings, the same as the editor's "Remove filler words" and "Remove pauses" smart actions. The audio and footage are cut, so the video gets shorter; the words stay in the transcript as cut regions. A scene group's \`fillerWords\` count in the video state says how many fillers are left; pauses show in the transcript as \`<pause seconds="N"/>\` tags, and those of a second or more are the ones cut. Only works on recordings: an AI voiceover has neither, so reword it with ${TOOL_NAMES.editAiVoiceover} instead. Call it only for what the user has asked to remove, or agreed to when you offered; don't use it on your own to shorten or polish a video.`;

    // A cleanup touches every scene of every recording, so the full diff would reprint the whole
    // transcript; the result summarises what changed instead.
    protected override readonly appendsVideoDiff = false;

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const { remove } = JSON.parse(toolCallArgs) as Partial<CleanUpRecordingArgs>;
                if (remove?.length === 1) {
                    return remove[0] === 'pauses' ? 'Removing pauses' : 'Removing filler words';
                }
            } catch {
                // Still streaming, or not JSON: fall through to the general name.
            }
        }
        return 'Cleaning up recording';
    }

    public get statusIcon(): string {
        return 'content_cut';
    }

    public async execute(args: CleanUpRecordingArgs, context: ToolExecutionContext) {
        const { parentActionGroup } = context;

        const targets = this.resolveTargets(args.scene_group_ids);
        if ('error' in targets) {
            return targets.error;
        }
        if (targets.sceneGroups.length === 0) {
            return 'Success: This video has no recorded voiceover or talking-head recordings, so there was nothing to clean up.';
        }

        const state = this.getState() as RootState;
        const missingEditor = targets.sceneGroups.find(
            (sceneGroup) => !getEditorFromSceneGroupId(state, sceneGroup.id),
        );
        if (missingEditor) {
            return `Error: The transcript for scene group ${sceneGroupDisplayId(this.luminary, missingEditor.id)} hasn't loaded yet, so it can't be cleaned up. Try again in a moment.`;
        }

        const { cutFillerWords, cutSilences } = makeOperationsCallable(
            { cutFillerWordsOperation, cutSilencesOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const removeFillerWords = args.remove.includes('filler_words');
        const removePauses = args.remove.includes('pauses');
        const durationBeforeMs = this.luminary.duration;
        let wordsRemoved = 0;
        let pauseMsRemoved = 0;
        try {
            for (const sceneGroup of targets.sceneGroups) {
                // Fillers first: each filler cut takes the gap around it, which can leave a
                // shorter pause than the one the pause cut would otherwise have measured.
                if (removeFillerWords) {
                    wordsRemoved += cutFillerWords(sceneGroup.id, false, parentActionGroup);
                }
                if (removePauses) {
                    pauseMsRemoved += cutSilences(
                        sceneGroup.id,
                        MIN_CUTTABLE_SILENCE_DURATION,
                        false,
                        parentActionGroup,
                    );
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error cleaning up the recording: ${errorMessage}`;
        }

        const sceneGroupCount = targets.sceneGroups.length;
        const scope = `${sceneGroupCount} ${pluralize(sceneGroupCount, 'recording')}`;
        const removed = [
            wordsRemoved > 0 && `${wordsRemoved} filler ${pluralize(wordsRemoved, 'word')}`,
            pauseMsRemoved > 0 && `${formatSeconds(pauseMsRemoved)} of pauses`,
        ].filter(Boolean);
        const notFound = [
            removeFillerWords && wordsRemoved === 0 && 'filler words',
            removePauses && pauseMsRemoved === 0 && 'pauses of a second or more',
        ].filter(Boolean);
        const notFoundNote =
            notFound.length > 0 ? ` There were no ${notFound.join(' or ')} to remove.` : '';
        if (removed.length === 0) {
            return `Success: There were no ${notFound.join(' or ')} to remove from ${scope}; nothing changed.`;
        }
        const removedMs = durationBeforeMs - this.luminary.duration;
        return `Success: Removed ${removed.join(' and ')} (${formatSeconds(removedMs)} in all) from ${scope}. The video is now ${formatSeconds(this.luminary.duration)} long.${notFoundNote}`;
    }

    private resolveTargets(
        sceneGroupIds: string[] | undefined,
    ): { sceneGroups: SceneGroup[] } | { error: string } {
        if (!sceneGroupIds?.length) {
            return { sceneGroups: this.luminary.sceneGroups.filter(isHumanVoicedrivenSceneGroup) };
        }
        const sceneGroups: SceneGroup[] = [];
        for (const sceneGroupId of sceneGroupIds) {
            const resolved = resolveSceneGroupById(this.luminary, sceneGroupId);
            if ('error' in resolved) {
                return { error: `Error: ${resolved.error}` };
            }
            const { sceneGroup } = resolved;
            if (isAIVoiceoverSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
                return {
                    error: `Error: Scene group ${sceneGroupDisplayId(this.luminary, sceneGroup.id)} is AI-generated speech, which has no filler words or pauses to cut. To change what it says, use ${TOOL_NAMES.editAiVoiceover}.`,
                };
            }
            if (!isHumanVoicedrivenSceneGroup(sceneGroup)) {
                return {
                    error: `Error: Scene group ${sceneGroupDisplayId(this.luminary, sceneGroup.id)} has no recording. Only recorded voiceover and talking-head scene groups can be cleaned up.`,
                };
            }
            if (!sceneGroups.includes(sceneGroup)) {
                sceneGroups.push(sceneGroup);
            }
        }
        return { sceneGroups };
    }
}
